import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Megaphone, Copy, MessageCircle, ExternalLink, Loader2, Info, FileDown } from "lucide-react";

export default function Landings() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);

  useEffect(() => {
    api.get("/landings/mine").then(({ data }) => setItems(data)).catch(() => setItems([]));
  }, []);

  // I lead arrivano intestati al partner che condivide la landing (?ref=<codice_collab>).
  const withRef = (path) => (user?.codice_collab ? `${path}?ref=${encodeURIComponent(user.codice_collab)}` : path);
  const fullUrl = (path) => (typeof window !== "undefined" ? `${window.location.origin}${withRef(path)}` : withRef(path));
  const brochureUrl = (code) => `${process.env.REACT_APP_BACKEND_URL}/api/public/products/${code}/brochure`;

  const copy = async (path) => {
    try { await navigator.clipboard.writeText(fullUrl(path)); toast.success("Link copiato"); }
    catch { toast.error("Copia non riuscita"); }
  };

  const wa = (l) =>
    `https://wa.me/?text=${encodeURIComponent(`Scopri ${l.name}: ${l.descrizione} ${fullUrl(l.path)}`)}`;

  if (items === null) {
    return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-forest" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="landings-page">
      <div>
        <h1 className="font-heading text-3xl font-semibold text-forest">Landing</h1>
        <p className="mt-1 text-muted-foreground">
          Le pagine informative del canale diretto Assietica autorizzate per te. Condividile con i tuoi contatti: i lead
          arrivano all'Agenzia.
        </p>
      </div>

      {items.length === 0 ? (
        <Card className="rounded-2xl border-border/60 p-10 text-center" data-testid="landings-empty">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-sage text-forest">
            <Info className="h-7 w-7" />
          </div>
          <h2 className="mt-4 font-heading text-lg font-semibold text-forest">Nessuna landing autorizzata</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Al momento non hai landing abilitate. Per la preventivazione procedi con il caricamento dei dati del cliente
            da «Nuovo preventivo».
          </p>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {items.map((l) => (
            <Card key={l.key} data-testid={`landing-card-${l.key}`} className="flex flex-col rounded-2xl border-border/60 p-5">
              <div className="flex items-start gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-forest text-white">
                  <Megaphone className="h-5 w-5" strokeWidth={1.75} />
                </div>
                <div className="min-w-0">
                  <h3 className="font-heading text-base font-semibold text-forest">{l.name}</h3>
                  <p className="mt-0.5 text-sm text-muted-foreground">{l.descrizione}</p>
                </div>
              </div>
              <div className="mt-3 truncate rounded-lg bg-[#F0F4F1] px-3 py-2 text-xs text-forest/70" title={fullUrl(l.path)}>
                {fullUrl(l.path)}
              </div>
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <Button size="sm" onClick={() => copy(l.path)} data-testid={`landing-copy-${l.key}`} className="gap-1.5 bg-forest hover:bg-forest/90">
                  <Copy className="h-3.5 w-3.5" /> Copia link
                </Button>
                <a href={wa(l)} target="_blank" rel="noopener noreferrer" data-testid={`landing-wa-${l.key}`}
                  className="inline-flex items-center gap-1.5 rounded-md border border-forest/30 px-3 py-1.5 text-sm font-medium text-forest transition-colors hover:bg-sage">
                  <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
                </a>
                <a href={withRef(l.path)} target="_blank" rel="noopener noreferrer" data-testid={`landing-open-${l.key}`}
                  className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm font-medium text-forest transition-colors hover:bg-sage">
                  <ExternalLink className="h-3.5 w-3.5" /> Apri
                </a>
                {l.product_code && (
                  <a href={brochureUrl(l.product_code)} target="_blank" rel="noopener noreferrer" data-testid={`landing-brochure-${l.key}`}
                    className="inline-flex items-center gap-1.5 rounded-md border border-terracotta/40 px-3 py-1.5 text-sm font-medium text-terracotta transition-colors hover:bg-terracotta/10">
                    <FileDown className="h-3.5 w-3.5" /> Brochure PDF
                  </a>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
